import { AddOnElemAndConfig } from "@robotical/ricjs";
import { useEffect, useState } from "react";
import AddOnItem from "../../../../components/disposables/AddonItem";
import LoadingSpinner from "../../../../components/disposables/LoadingSpinner";
import Logo from "../../../../components/disposables/Logo";
import { getHWElemTypeStr } from "../../../../utils/RICAddonsMap";
import styles from "./styles.module.css";
import Logger from "../../../../services/logger/Logger";
import { NewRobotIdE, useConnectedRafts } from "../../../../store/SelectedRaftContext";
import { useTranslation } from "react-i18next";

const SHOW_LOGS = true;
const TAG = "Addons";

export default function Addons() {
  const { t } = useTranslation();

  const { connectedRafts } = useConnectedRafts();
  const selectedRaft = connectedRafts.find(connectedRaft => connectedRaft.isSelected && connectedRaft.id !== NewRobotIdE.NEW);

  const [isLoading, setIsLoading] = useState(true);
  const [addOnOpened, setAddOnOpened] = useState("");
  const [addonDeleted, setAddonDeleted] = useState(false);

  const [addOnsFound, setAddOnsFound] = useState(
    new Array<AddOnElemAndConfig>()
  );

  const getAddOnInfo = async () => {
    if (!selectedRaft) {
      Logger.error(SHOW_LOGS, TAG, "No selected raft found");
      return;
    }
    try {
      //First the app gets the addon/list info
      //Which is previously saved info and not what
      //is currently connected to the robot
      const hwElemList = await window.applicationManager.getConnectedAddOnsMarty(selectedRaft.id);
      Logger.info(SHOW_LOGS, TAG, `Found hardware elements ${hwElemList}`);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      Logger.info(SHOW_LOGS, TAG, `Getting add-on configs`);
      const addOnList = await window.applicationManager.getAddOnConfigsMarty(selectedRaft.id);
      Logger.info(SHOW_LOGS, TAG, `Retrieved add-on configuration list ${addOnList}`);

      const addOnElemAndConfigList = new Array<AddOnElemAndConfig>();
      let elemIdx = 0;

      // List all connected add-ons
      for (const hwElemRec of hwElemList) {
        if (hwElemRec.type !== "RSAddOn" || hwElemRec.SN.length === 0) {
          continue;
        }
        let matchFound = false;
        for (const addOnRec of addOnList.addons) {
          if (addOnRec.SN === hwElemRec.SN) {
            // item is on addon list and detected as connected to the robot
            addOnElemAndConfigList.push(
              new AddOnElemAndConfig(addOnRec, hwElemRec, elemIdx++)
            );
            matchFound = true;
            break;
          }
        }
        if (!matchFound) {
          // item is present but has not been configured to addon list
          addOnElemAndConfigList.push(
            new AddOnElemAndConfig(null, hwElemRec, elemIdx++)
          );
        }
      }

      // Also list add-ons from saved data, but not plugged in
      for (const addOnRec of addOnList.addons) {
        let itemPresent = false;

        for (const addOnElemAndConfig of addOnElemAndConfigList) {
          if (addOnElemAndConfig.SN === addOnRec.SN) {
            itemPresent = true;
            break;
          }
        }
        if (!itemPresent) {
          // item is in addonlist, but not physically connected to the robot
          addOnElemAndConfigList.push(
            new AddOnElemAndConfig(addOnRec, null, elemIdx++)
          );
        }
      }

      // Set found addons into local state
      setAddOnsFound(addOnElemAndConfigList);

      Logger.info(SHOW_LOGS, TAG, `Found add-ons ${addOnElemAndConfigList}`);
      // debugger;
    } catch (error) {
      Logger.error(SHOW_LOGS, TAG, `Failed to get add-on info ${error}`);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getAddOnInfo();
  }, []);

  const onPressAddOn = async (item: AddOnElemAndConfig) => {
    if (!selectedRaft) {
      Logger.error(SHOW_LOGS, TAG, "No selected raft found");
      return;
    }
    try {
      Logger.info(SHOW_LOGS, TAG, `User pressed add-on ${item.SN}`);
      await window.applicationManager.identifyAddOnMarty(selectedRaft.id, item.name);
      if (addOnOpened === item.id) {
        setAddOnOpened("");
      } else {
        setAddOnOpened(item.id);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const onPressSetName = async (
    serialNo: string | undefined,
    newName: string
  ) => {
    if (!selectedRaft) {
      Logger.error(SHOW_LOGS, TAG, "No selected raft found");
      return;
    }
    Logger.info(SHOW_LOGS, TAG, `Pressed set name serialNo ${serialNo} name ${newName}`);
    if (serialNo !== undefined) {
      await window.applicationManager.setAddOnConfigMarty(selectedRaft.id, serialNo, newName);
      setAddOnOpened("");
      getAddOnInfo();
    }
  };

  const onPressDelete = async (item: AddOnElemAndConfig) => {
    if (!selectedRaft) {
      Logger.error(SHOW_LOGS, TAG, "No selected raft found");
      return;
    }
    Logger.info(SHOW_LOGS, TAG, `Pressed delete serialNo ${item.SN}`);
    if (item.SN !== undefined) {
      await window.applicationManager.deleteAddOnMarty(selectedRaft.id, item.SN);
      setAddonDeleted(true);
      setAddOnOpened("");
      getAddOnInfo();
    }
  };

  const addonsListJSX = addOnsFound.map((addon) => {
    return (
      <AddOnItem
        key={addon.id}
        name={addon.name}
        whoami={addon.hwElemRec?.whoAmI}
        isConnected={addon.isConnected}
        isConfigured={addon.isConfigured}
        onPress={() => onPressAddOn(addon)}
        isOpen={addOnOpened === addon.id}
        onPressSetName={(serialNo, newName) =>
          onPressSetName(serialNo, newName)
        }
        onPressDelete={() => onPressDelete(addon)}
        addOnType={getHWElemTypeStr(addon.hwElemRec?.whoAmI)}
        versionStr={addon.hwElemRec?.versionStr}
        serialNo={addon.SN}
      />
    );
  });

  return (
    <div className={styles["addons-screen-container"]}>
      <div className={styles["addons-screen-logo-container"]}>
        <Logo />
      </div>
      <h2 className={styles["addons-screen-title"]}>{t("addons.manage_title")}</h2>
      <p className={styles["addons-screen-paragraph"]}>
        {t("addons.plug_in")}
      </p>
      <p className={styles["addons-screen-notice"]}>
        {addonDeleted ? t("addons.restart_notice") : ""}
      </p>
      <div
        className={styles["addons-screen-addons-list-container"]}
        style={{ width: isLoading ? "initial" : "100%" }}
      >
        {isLoading ? <LoadingSpinner /> : addonsListJSX}
      </div>
    </div>
  );
}
